import { motion } from 'motion/react';

/**
 * Animated hamburger ↔ close icon.
 *
 * Three horizontal bars morph into an X via GPU-composited
 * transforms (rotate + translateY). Transition: 300ms,
 * cubic-bezier(0.4, 0, 0.2, 1) — no spring, no overshoot.
 */

const EASE = [0.4, 0, 0.2, 1] as const;
const DURATION = 0.3;

interface HamburgerIconProps {
  /** When true the icon renders as a close (X) cross. */
  isOpen: boolean;
  /** Icon dimensions — defaults to 24. */
  size?: number;
  className?: string;
  onClick?: () => void;
}

export const HamburgerIcon = ({ isOpen, size = 30, className, onClick }: HamburgerIconProps) => {
  const barWidth = 20;
  const barX = (size - barWidth) / 2;  /* 5px */
  const centerY = size / 2;            /* 15px */
  const barSpacing = 6.25;             /* 8.75→15→21.25 */

  return (
    <button
      className="cursor-pointer p-0 border-0 bg-transparent lg:hidden"
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      onClick={onClick}
    >
      <motion.svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden
        initial={false}
      >
        {/* Top bar → rotates +45° around center */}
        <motion.line
          x1={barX}
          x2={barX + barWidth}
          stroke="white"
          strokeWidth="1.25"
          strokeLinecap="round"
          animate={{
            y1: isOpen ? centerY : centerY - barSpacing,
            y2: isOpen ? centerY : centerY - barSpacing,
            rotate: isOpen ? 45 : 0,
          }}
          transition={{ duration: DURATION, ease: EASE }}
          style={{ originX: '50%', originY: '50%' }}
          initial={false}
        />

        {/* Middle bar → fades out */}
        <motion.line
          x1={barX}
          x2={barX + barWidth}
          y1={centerY}
          y2={centerY}
          stroke="white"
          strokeWidth="1.25"
          strokeLinecap="round"
          animate={{ opacity: isOpen ? 0 : 1 }}
          transition={{ duration: DURATION * 0.5, ease: EASE }}
          initial={false}
        />

        {/* Bottom bar → rotates −45° around center */}
        <motion.line
          x1={barX}
          x2={barX + barWidth}
          stroke="white"
          strokeWidth="1.25"
          strokeLinecap="round"
          animate={{
            y1: isOpen ? centerY : centerY + barSpacing,
            y2: isOpen ? centerY : centerY + barSpacing,
            rotate: isOpen ? -45 : 0,
          }}
          transition={{ duration: DURATION, ease: EASE }}
          style={{ originX: '50%', originY: '50%' }}
          initial={false}
        />
      </motion.svg>
    </button>
  );
};
